import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import {
  IconBan,
  IconBell,
  IconBellRinging,
  IconCircleCheck,
  IconCircleX,
  IconFileInvoice,
  IconTrash,
  IconWritingSign,
} from "@tabler/icons-react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import {
  Group,
  IconAction,
  ListaItemsSkeleton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastError } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";
import {
  contarMisAvisosNoVistos,
  contarMisNotificacionesNoVistas,
  listarMisAvisos,
  listarMisNotificaciones,
  marcarMisAvisosVistos,
  marcarMisNotificacionesVistas,
  ocultarMiAviso,
  ocultarMiNotificacion,
  type AvisoApp,
  type Notificacion,
  type TipoNotificacion,
} from "../services/notificaciones.api";
import { destinoDeEnlace, hayFuentePendiente, totalNoVistas } from "../utils/avisos";

// --- Íconos por tipo (semánticos · mismos acentos que los estados) ---------------

const ICONO_NOTIF: Record<TipoNotificacion, ReactNode> = {
  AutorizacionNivel: <IconWritingSign size={16} stroke={1.7} className="text-azulProfundo-600" />,
  AutorizacionCompleta: <IconCircleCheck size={16} stroke={1.7} className="text-exito-600" />,
  Rechazo: <IconCircleX size={16} stroke={1.7} className="text-peligro-600" />,
  Baja: <IconBan size={16} stroke={1.7} className="text-carmin-600" />,
  Contabilizacion: <IconFileInvoice size={16} stroke={1.7} className="text-exito-700" />,
};

// --- Modelo unificado del panel ---------------------------------------------------
// La campana fusiona el feed del CORE (autorizaciones/rechazos/bajas/contabilización de
// solicitudes de pago) con los avisos del canal Campana del módulo Notificaciones (que ya
// incluyen las solicitudes de catálogo · flujo de sistema «Solicitudes de catálogo»).

interface ItemCampana {
  key: string;
  fecha: string;
  noVista: boolean;
  icono: ReactNode;
  contenido: ReactNode;
  navegar: (() => void) | null;
  eliminar: (() => void) | null;
  eliminando: boolean;
  etiquetaEliminar: string;
}

function TextoAviso({ a }: { a: AvisoApp }) {
  return (
    <>
      <Text size="sm" fw={600} style={{ lineHeight: 1.35 }}>
        {a.titulo}
      </Text>
      <Text size="sm" style={{ lineHeight: 1.35 }}>
        {a.texto}
      </Text>
      <Text size="xs" c="dimmed">
        {dayjs(a.creadoAt).format("DD/MM/YYYY HH:mm")}
      </Text>
    </>
  );
}

function TextoNotificacion({ n }: { n: Notificacion }) {
  return (
    <>
      <Text size="sm" style={{ lineHeight: 1.35 }}>
        {n.mensaje}
      </Text>
      <Text size="xs" c="dimmed">
        {dayjs(n.creadaAt).format("DD/MM/YYYY HH:mm")}
      </Text>
    </>
  );
}

/**
 * Campana de notificaciones del header · panel ÚNICO con todos los tipos:
 * autorizaciones por nivel, autorización completa, rechazos, bajas y
 * contabilización (feed del CORE) + avisos del canal Campana del módulo
 * Notificaciones (hotspot 111 · los que el administrador configura en
 * /admin/notificaciones, incluidas las solicitudes de catálogo). El badge suma las
 * NO vistas de ambas fuentes (polling 60s); abrir el panel las marca vistas.
 * Cada item navega a su solicitud o al enlace del aviso, y se puede ocultar.
 */
export function NotificacionesCampana() {
  const navigate = useNavigate();
  const router = useRouter();
  const qc = useQueryClient();
  // Hotspot 99 · cada fuente se consulta solo con el permiso que exige su endpoint
  // (`notificaciones.ver`) · sin él no se dispara (ni un 403).
  const misPermisos = useUserPermisos();
  const veNotificaciones = misPermisos.includes(PERMISOS.notificacionesVer);
  // Hotspot 111 · los avisos del canal Campana (/api/v1/avisos) solo exigen sesión: cada
  // usuario ve los SUYOS, y un flujo puede dirigirlos a cualquier rol o usuario.
  const veAvisos = true;

  const { data: notifNoVistas } = useQuery({
    queryKey: qk.misNotificacionesCount,
    queryFn: contarMisNotificacionesNoVistas,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: veNotificaciones,
  });

  const { data: avisosNoVistos } = useQuery({
    queryKey: qk.misAvisosCount,
    queryFn: contarMisAvisosNoVistos,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: veAvisos,
  });

  const { data: notificaciones, refetch: refetchNotificaciones, status: statusNotificaciones } = useQuery({
    queryKey: qk.misNotificaciones,
    queryFn: listarMisNotificaciones,
    enabled: false, // solo al abrir el panel
  });
  const { data: avisos, refetch: refetchAvisos, status: statusAvisos } = useQuery({
    queryKey: qk.misAvisos,
    queryFn: listarMisAvisos,
    enabled: false, // solo al abrir el panel
  });

  const marcarVistasNotif = useMutation({
    mutationFn: marcarMisNotificacionesVistas,
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.misNotificacionesCount }),
  });
  const marcarVistosAvisos = useMutation({
    mutationFn: marcarMisAvisosVistos,
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.misAvisosCount }),
  });

  const alErrorEliminar = (error: unknown) => {
    toastError({ title: "No se pudo eliminar", message: extractApiMessage(error, "Intenta de nuevo.") });
  };
  const ocultarAviso = useMutation({
    mutationFn: ocultarMiAviso,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.misAvisosCount });
      void refetchAvisos();
    },
    onError: alErrorEliminar,
  });
  const eliminarNotif = useMutation({
    mutationFn: ocultarMiNotificacion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.misNotificacionesCount });
      void refetchNotificaciones();
    },
    onError: alErrorEliminar,
  });

  const alAbrir = (opened: boolean) => {
    if (!opened) return;
    if (veNotificaciones) {
      void refetchNotificaciones();
      if ((notifNoVistas ?? 0) > 0) marcarVistasNotif.mutate();
    }
    if (veAvisos) {
      void refetchAvisos();
      if ((avisosNoVistos ?? 0) > 0) marcarVistosAvisos.mutate();
    }
  };

  const abrirEnlace = (enlace: string | null) => {
    const destino = destinoDeEnlace(enlace, window.location.origin);
    if (!destino) return null;
    return destino.tipo === "interno"
      ? () => router.history.push(destino.ruta)
      : () => void window.open(destino.url, "_blank", "noopener,noreferrer");
  };

  const itemsAvisos: ItemCampana[] = (avisos ?? [])
    .map((a): ItemCampana => ({
      key: `a-${a.id}`,
      fecha: a.creadoAt,
      noVista: !a.vistoAt,
      icono: <IconBellRinging size={16} stroke={1.7} className="text-multiva-600" />,
      contenido: <TextoAviso a={a} />,
      navegar: abrirEnlace(a.enlace),
      eliminar: () => ocultarAviso.mutate(a.id),
      eliminando: ocultarAviso.isPending && ocultarAviso.variables === a.id,
      etiquetaEliminar: `Ocultar aviso «${a.titulo}»`,
    }))
    .sort((a, b) => {
      if (a.noVista !== b.noVista) return a.noVista ? -1 : 1;
      return dayjs(b.fecha).valueOf() - dayjs(a.fecha).valueOf();
    });

  // --- Fusión de ambas fuentes · no vistas primero, luego recientes ---------------

  const items: ItemCampana[] = [
    ...(notificaciones ?? []).map((n): ItemCampana => ({
      key: `n-${n.id}`,
      fecha: n.creadaAt,
      noVista: !n.vistaAt,
      icono: ICONO_NOTIF[n.tipo],
      contenido: <TextoNotificacion n={n} />,
      navegar: () => void navigate({ to: "/solicitudes/$id", params: { id: n.solicitudId } }),
      eliminar: () => eliminarNotif.mutate(n.id),
      eliminando: eliminarNotif.isPending && eliminarNotif.variables === n.id,
      etiquetaEliminar: `Eliminar notificación de la solicitud #${n.numeroSolicitud}`,
    })),
  ].sort((a, b) => {
    if (a.noVista !== b.noVista) return a.noVista ? -1 : 1;
    return dayjs(b.fecha).valueOf() - dayjs(a.fecha).valueOf();
  });

  const noVistasTotal = totalNoVistas(
    veNotificaciones ? notifNoVistas : undefined,
    veAvisos ? avisosNoVistos : undefined
  );

  const renderItem = (item: ItemCampana) => (
    <Group
      key={item.key}
      gap={4}
      wrap="nowrap"
      align="flex-start"
      className="rounded-lg p-2"
      style={{
        backgroundColor: item.noVista
          ? "light-dark(var(--color-multiva-50), color-mix(in srgb, var(--color-multiva-600) 20%, transparent))"
          : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => item.navegar?.()}
        disabled={!item.navegar}
        className="flex-1 text-left disabled:cursor-default"
        style={{ cursor: item.navegar ? "pointer" : "default" }}
      >
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <div className="pt-0.5">{item.icono}</div>
          <Stack gap={2} className="flex-1">
            {item.contenido}
          </Stack>
        </Group>
      </button>
      {item.eliminar && (
        <IconAction label={item.etiquetaEliminar} size="sm" loading={item.eliminando} onClick={item.eliminar}>
          <IconTrash size={14} stroke={1.7} />
        </IconAction>
      )}
    </Group>
  );

  const hayAmbasSecciones = itemsAvisos.length > 0 && items.length > 0;
  const cargandoPanel = hayFuentePendiente([
    { habilitada: veAvisos, status: statusAvisos },
    { habilitada: veNotificaciones, status: statusNotificaciones },
  ]);

  return (
    <Popover onOpenChange={alAbrir}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notificaciones"
          className="relative flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted"
        >
          <IconBell size={18} />
          {noVistasTotal > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-peligro-600 px-1 text-[10px] font-semibold text-white ring-2 ring-card">
              {noVistasTotal > 99 ? "99+" : noVistasTotal}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-95 p-1">
        <Group justify="space-between" className="px-2 py-1">
          <Text size="sm" fw={600}>
            Notificaciones
          </Text>
        </Group>
        <div className="max-h-90 overflow-y-auto">
          <Stack gap={4} className="p-1">
            {cargandoPanel ? (
              <ListaItemsSkeleton items={4} />
            ) : itemsAvisos.length === 0 && items.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" className="py-3">
                Sin notificaciones · aquí verás los avisos del portal y las autorizaciones,
                rechazos y avisos de tus solicitudes de pago y de tus solicitudes de catálogo.
              </Text>
            ) : (
              <>
                {itemsAvisos.length > 0 && (
                  <>
                    {hayAmbasSecciones && (
                      <Text size="xs" fw={600} c="dimmed" className="px-2 pt-1 uppercase tracking-wide">
                        Avisos
                      </Text>
                    )}
                    {itemsAvisos.map(renderItem)}
                  </>
                )}
                {items.length > 0 && (
                  <>
                    {hayAmbasSecciones && (
                      <Text size="xs" fw={600} c="dimmed" className="px-2 pt-2 uppercase tracking-wide">
                        Solicitudes
                      </Text>
                    )}
                    {items.map(renderItem)}
                  </>
                )}
              </>
            )}
          </Stack>
        </div>
      </PopoverContent>
    </Popover>
  );
}
